import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { QrImage } from "@/components/QrImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Copy, Check, ArrowDownToLine } from "lucide-react";

export default function UserDeposit() {
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/user/payment-info").then((r) => setInfo(r.data)).catch(() => setInfo({ upi_id: "", has_qr: false }));
    api.get("/user/deposits").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = () => {
    if (!info?.upi_id) return;
    navigator.clipboard.writeText(info.upi_id);
    setCopied(true); toast.success("UPI ID copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/user/deposits", { amount: Number(amount), utr });
      toast.success("Deposit request submitted for verification");
      setAmount(""); setUtr(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Deposit Funds" desc="Pay to your assigned UPI/QR, then submit the UTR for verification." />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="vp-card rounded-xl p-6">
          <h3 className="font-semibold text-lg mb-4">1 · Pay to your UPI</h3>
          {!info ? <div className="grid place-items-center h-52"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div> : (
            <div className="flex flex-col sm:flex-row gap-5 items-center">
              <QrImage className="h-44 w-44 flex-shrink-0" />
              <div className="flex-1 w-full">
                {info.upi_id ? (
                  <>
                    <Label className="text-slate-400 text-xs uppercase tracking-widest">Your UPI ID</Label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3">
                      <span className="font-mono text-emerald-300 break-all flex-1" data-testid="user-upi-id">{info.upi_id}</span>
                      <Button size="icon" variant="ghost" onClick={copy} data-testid="copy-upi-btn" className="h-8 w-8 text-slate-300 hover:text-white">{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}</Button>
                    </div>
                  </>
                ) : <p className="text-sm text-amber-300">No UPI ID assigned yet. Please contact support.</p>}
                <p className="mt-3 text-xs text-slate-500">Scan the QR or use the UPI ID above to complete your payment, then enter the UTR/reference on the right.</p>
              </div>
            </div>
          )}
        </div>

        <div className="vp-card rounded-xl p-6">
          <h3 className="font-semibold text-lg mb-4">2 · Submit deposit request</h3>
          <div className="space-y-4">
            <div><Label className="text-slate-300">Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deposit-amount-input" placeholder="Enter amount paid" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11" /></div>
            <div><Label className="text-slate-300">UTR / Reference Number</Label><Input value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="deposit-utr-input" placeholder="12-digit UTR from your bank app" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11 font-mono" /></div>
            <Button onClick={submit} disabled={busy || !amount || !utr} data-testid="submit-deposit-btn" className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ArrowDownToLine className="mr-2 h-5 w-5" />Submit for Verification</>}
            </Button>
            <p className="text-xs text-slate-500">Your balance is credited only after an admin verifies the payment.</p>
          </div>
        </div>
      </div>

      <h3 className="font-semibold text-lg mt-8 mb-3">Your Deposits</h3>
      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-white/5 hover:bg-transparent"><TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">UTR</TableHead><TableHead className="text-slate-400">Date (IST)</TableHead><TableHead className="text-slate-400">Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">No deposits yet.</TableCell></TableRow> :
                rows.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`my-deposit-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell className="font-mono text-emerald-300">{formatINR(t.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.utr || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell><StatusBadge status={t.status} />{t.status === "rejected" && t.remark ? <div className="text-xs text-rose-300 mt-1">{t.remark}</div> : null}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
